// Shared presentation theme + audience-template model.
//
// The presentation config carries two first-class, planner-visible fields that
// were previously written by the AI `update_theme` operation but never read by
// any renderer (plan Section 6 area 5):
//   - `config.style.theme`     -> a named visual theme (colors + heading font)
//   - `config.style.template`  -> a named audience template (Investor / Board /
//                                  Owner / Full) that selects a curated block
//                                  set + a default theme.
//
// This module is the single source of truth for both so the live preview
// (`QuickBooksCfoPresentationTab`), the export HTML / slide deck
// (`QuickBooksCfoPresentationExport`), and the backend `style` normalization
// stay consistent. Theme tokens are emitted as CSS custom properties so the
// preview CSS and the exported `:root` consume the exact same values.

export type PresentationThemeId = 'classic' | 'executive' | 'midnight' | 'warm' | 'mono'
export type PresentationTemplateId = 'full' | 'investor' | 'board' | 'owner'

export type PresentationThemeTokens = {
  pageBg: string
  mastheadBg: string
  mastheadText: string
  mastheadMeta: string
  eyebrow: string
  accent: string
  kpiAccent: string
  headingFont: string
}

export type PresentationThemeDef = {
  id: PresentationThemeId
  label: string
  description: string
  tokens: PresentationThemeTokens
}

export type PresentationTemplateDef = {
  id: PresentationTemplateId
  label: string
  description: string
  theme: PresentationThemeId
  /** Report block ids that are enabled for this audience. Empty => keep all. */
  blockIds: string[]
}

const HEADING_SERIF = 'Georgia, "Times New Roman", serif'
const HEADING_SANS = 'Inter, "Segoe UI", system-ui, -apple-system, sans-serif'

export const PRESENTATION_THEMES: Record<PresentationThemeId, PresentationThemeDef> = {
  classic: {
    id: 'classic',
    label: 'Classic Gold',
    description: 'Navy masthead with a gold accent rule. The original report look.',
    tokens: {
      pageBg: '#f4f0e8',
      mastheadBg: '#132238',
      mastheadText: '#ffffff',
      mastheadMeta: '#d7dee8',
      eyebrow: '#d9b869',
      accent: '#b8892f',
      kpiAccent: '#2f855a',
      headingFont: HEADING_SERIF,
    },
  },
  executive: {
    id: 'executive',
    label: 'Executive Teal',
    description: 'Slate masthead with a teal accent. Calm, investor-ready.',
    tokens: {
      pageBg: '#eef2f5',
      mastheadBg: '#1e293b',
      mastheadText: '#ffffff',
      mastheadMeta: '#cbd5e1',
      eyebrow: '#5eead4',
      accent: '#0d9488',
      kpiAccent: '#0d9488',
      headingFont: HEADING_SANS,
    },
  },
  midnight: {
    id: 'midnight',
    label: 'Midnight Board',
    description: 'High-contrast dark navy with a sky accent for board packets.',
    tokens: {
      pageBg: '#e9eef5',
      mastheadBg: '#0f172a',
      mastheadText: '#f8fafc',
      mastheadMeta: '#c7d2e0',
      eyebrow: '#7dd3fc',
      accent: '#38bdf8',
      kpiAccent: '#2563eb',
      headingFont: HEADING_SANS,
    },
  },
  warm: {
    id: 'warm',
    label: 'Warm Owner',
    description: 'Warm sienna masthead, approachable for an owner review.',
    tokens: {
      pageBg: '#fdf6ef',
      mastheadBg: '#7c2d12',
      mastheadText: '#fff7ed',
      mastheadMeta: '#fcd9b8',
      eyebrow: '#fdba74',
      accent: '#ea580c',
      kpiAccent: '#c2410c',
      headingFont: HEADING_SERIF,
    },
  },
  mono: {
    id: 'mono',
    label: 'Minimal Mono',
    description: 'Neutral grayscale for a clean, print-first look.',
    tokens: {
      pageBg: '#f4f4f5',
      mastheadBg: '#111827',
      mastheadText: '#ffffff',
      mastheadMeta: '#d4d4d8',
      eyebrow: '#a1a1aa',
      accent: '#52525b',
      kpiAccent: '#3f3f46',
      headingFont: HEADING_SANS,
    },
  },
}

export const PRESENTATION_TEMPLATES: Record<PresentationTemplateId, PresentationTemplateDef> = {
  full: {
    id: 'full',
    label: 'Full Review',
    description: 'Every section enabled — the complete management review.',
    theme: 'classic',
    blockIds: [],
  },
  investor: {
    id: 'investor',
    label: 'Investor',
    description: 'Growth, profitability, and the balance sheet for an investor update.',
    theme: 'executive',
    blockIds: [
      'home-kpis',
      'home-summary',
      'home-waterfalls',
      'home-pnl-rollup',
      'home-quarterly-trend',
      'pnl-kpis',
      'pnl-rollup',
      'pnl-quarterly-trend',
      'balance-sheet-rollup',
      'cash-flow-rollup',
      'actions-list',
    ],
  },
  board: {
    id: 'board',
    label: 'Board',
    description: 'A concise governance packet across all three statements + A/R.',
    theme: 'midnight',
    blockIds: [
      'home-kpis',
      'home-summary',
      'home-waterfalls',
      'home-pnl-rollup',
      'pnl-rollup',
      'balance-sheet-rollup',
      'cash-flow-rollup',
      'ar-summary',
      'actions-list',
    ],
  },
  owner: {
    id: 'owner',
    label: 'Owner',
    description: 'Operational detail an owner cares about: P&L detail, cash, A/R.',
    theme: 'warm',
    blockIds: [
      'home-kpis',
      'home-summary',
      'home-pnl-rollup',
      'pnl-rollup',
      'pnl-detail',
      'cash-flow-rollup',
      'cash-flow-detail',
      'ar-summary',
      'actions-list',
    ],
  },
}

export const DEFAULT_PRESENTATION_THEME_ID: PresentationThemeId = 'classic'
export const DEFAULT_PRESENTATION_TEMPLATE_ID: PresentationTemplateId = 'full'

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

export const isPresentationThemeId = (value: any): value is PresentationThemeId =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(PRESENTATION_THEMES, value)

export const isPresentationTemplateId = (value: any): value is PresentationTemplateId =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(PRESENTATION_TEMPLATES, value)

export type ResolvedPresentationTheme = {
  themeId: PresentationThemeId
  templateId: PresentationTemplateId
  tokens: PresentationThemeTokens
  /** CSS custom properties consumed by both the preview CSS and export `:root`. */
  cssVars: Record<string, string>
}

/**
 * Resolve the active theme from a presentation config's `style` object.
 *
 * Precedence for the theme: an explicit `style.theme` wins; otherwise the
 * audience template's default theme is used; otherwise `classic`. An optional
 * `style.accent` hex override replaces the accent + eyebrow tokens so a user
 * can brand a known theme without authoring a whole palette.
 */
export const resolvePresentationTheme = (style: any): ResolvedPresentationTheme => {
  const src = style && typeof style === 'object' ? style : {}
  const templateId: PresentationTemplateId = isPresentationTemplateId(src.template)
    ? src.template
    : DEFAULT_PRESENTATION_TEMPLATE_ID
  const themeId: PresentationThemeId = isPresentationThemeId(src.theme)
    ? src.theme
    : PRESENTATION_TEMPLATES[templateId].theme
  const base = PRESENTATION_THEMES[themeId].tokens
  const accentOverride = typeof src.accent === 'string' && HEX_COLOR.test(src.accent.trim())
    ? src.accent.trim()
    : null
  const tokens: PresentationThemeTokens = accentOverride
    ? { ...base, accent: accentOverride, kpiAccent: accentOverride }
    : { ...base }
  return {
    themeId,
    templateId,
    tokens,
    cssVars: {
      '--fgcfo-page-bg': tokens.pageBg,
      '--fgcfo-masthead-bg': tokens.mastheadBg,
      '--fgcfo-masthead-text': tokens.mastheadText,
      '--fgcfo-masthead-meta': tokens.mastheadMeta,
      '--fgcfo-eyebrow': tokens.eyebrow,
      '--fgcfo-accent': tokens.accent,
      '--fgcfo-kpi-accent': tokens.kpiAccent,
      '--fgcfo-heading-font': tokens.headingFont,
    },
  }
}

/** Serialize resolved CSS vars into a `key:value;` declaration block. */
export const presentationThemeCssText = (theme: ResolvedPresentationTheme): string =>
  Object.entries(theme.cssVars)
    .map(([key, value]) => `${key}: ${value};`)
    .join(' ')

/**
 * Apply an audience template to a list of report blocks: enable blocks named in
 * the template and disable the rest (blocks are preserved, not removed, so a
 * user can switch back). `full` (empty blockIds) re-enables everything.
 */
export const applyTemplateToReportBlocks = <T extends { id: string; enabled?: boolean }>(
  blocks: T[],
  templateId: PresentationTemplateId,
): T[] => {
  const template = PRESENTATION_TEMPLATES[templateId] || PRESENTATION_TEMPLATES.full
  if (!template.blockIds.length) {
    return blocks.map((block) => ({ ...block, enabled: true }))
  }
  const allow = new Set(template.blockIds)
  return blocks.map((block) => ({ ...block, enabled: allow.has(block.id) }))
}
